import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Loader2 } from 'lucide-react';
import logo from '../../../assets/altadia-logo.png'; // Logo import

type AuthMode = 'login' | 'register';

export function LoginPage() {
  const { signInWithGoogle, signInWithEmail, registerWithEmail, loading, error } = useAuth();
  const [useEmailLogin, setUseEmailLogin] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!email || !password) {
      setValidationError('Please fill in all required fields');
      return;
    }

    if (authMode === 'register') {
      if (password !== confirmPassword) {
        setValidationError('Passwords do not match');
        return;
      }
      if (password.length < 6) {
        setValidationError('Password must be at least 6 characters');
        return;
      }
      
      const result = await registerWithEmail(email, password);
      if (!result.success) {
        // Error is handled by useAuth hook
      }
    } else {
      await signInWithEmail(email, password);
    }
  };

  const toggleMode = () => {
    setAuthMode(prev => prev === 'login' ? 'register' : 'login');
    setValidationError(null);
    setPassword('');
    setConfirmPassword('');
  };

  // Main container with a light gray background for a clean look
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      {/* White card for maximum readability and a professional feel */}
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-xl">
        <div className="text-center mb-8">
          <img src={logo} alt="Company Logo" className="h-20 w-auto mx-auto mb-4" />
          {/* Dark corporate gray for the main title for strong contrast and professional look */}
          <h1 className="text-3xl font-bold text-[#454240] mb-2">
            Commercial Action Tracker
          </h1>
          <p className="text-gray-600">
            {authMode === 'login' 
              ? 'Sign in to manage your commercial actions' 
              : 'Create an account to get started'}
          </p>
        </div>

        {!useEmailLogin ? (
          // Google & Email options
          <>
            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="text-gray-900 font-medium">
                    Sign in with Google
                  </span>
                </>
              )}
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or</span>
              </div>
            </div>

            {/* Corporate red for the primary alternative action */}
            <button
              onClick={() => setUseEmailLogin(true)}
              className="w-full px-4 py-3 text-white bg-[#eb4a4a] rounded-lg hover:bg-[#d44343] transition-colors font-medium"
            >
              Continue with Email
            </button>
          </>
        ) : (
          // Email/Password Form
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  // Red focus ring for brand consistency
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
                  placeholder="your.email@company.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
                  placeholder="••••••••"
                />
              </div>

              {authMode === 'register' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required={authMode === 'register'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#eb4a4a] focus:border-[#eb4a4a]"
                    placeholder="••••••••"
                  />
                </div>
              )}

              {validationError && (
                <p className="text-sm text-red-600">{validationError}</p>
              )}

              {/* Main action button is corporate red */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#eb4a4a] text-white rounded-lg hover:bg-[#d44343] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>{authMode === 'login' ? 'Sign In' : 'Create Account'}</span>
                )}
              </button>
            </form>

            <div className="flex flex-col gap-2 mt-4 text-center">
              <p className="text-sm text-gray-600">
                {authMode === 'login' ? "Don't have an account?" : "Already have an account?"}{' '}
                {/* Red for interactive links */}
                <button
                  onClick={toggleMode}
                  className="font-medium text-[#eb4a4a] hover:text-[#d44343] hover:underline"
                >
                  {authMode === 'login' ? 'Sign up' : 'Log in'}
                </button>
              </p>
              
              <button
                onClick={() => {
                  setUseEmailLogin(false);
                  setAuthMode('login');
                  setValidationError(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                ← Back to Google Sign In
              </button>
            </div>
          </>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}